import { useState, useCallback, useRef, useEffect } from 'react';
import { useFastPan } from './useFastPan';
import { isPannableTarget, isContextMenuTarget } from './utils/canvasTargetDetection';
import { isPointerOverPortaledOverlayFromEvent } from './utils/overlayPointerGuard';
import { screenToCanvas as screenToCanvasUtil, computeEdgeAutoPanDelta } from './utils/canvasUtils';
import {
  isMissionToSidebarDragActive,
  updateMissionToSidebarPointer,
} from '../../../../utils/missionToSidebarBridge';

/**
 * useFlowchartEvents Hook
 * 
 * Handles combined mouse event orchestration for the flowchart.
 * Coordinates between selection, drag, pan, and connection behaviors.
 * 
 * PERFORMANCE: Uses direct DOM manipulation for panning to bypass React re-renders.
 */
export function useFlowchartEvents({
  containerRef,
  screenToCanvas,
  // Pan
  panHook,
  setPan,
  setPanAnimated,
  constrainPan,
  cancelPanCorrection,
  isPanningRef,
  zoomRef,
  // Selection
  startSelectionBox,
  updateSelectionBox,
  completeSelectionBox,
  selectNode,
  setSelectedNodes,
  setSelectedEdge,
  clearSelection,
  isSelectingBox,
  // Drag
  potentialDragNode,
  checkDragThreshold,
  updateDragPosition,
  completeDrag,
  isDragging,
  draggingNodeRef,
  // Connection
  isConnecting,
  connectingFrom,
  updateConnectionTarget,
  completeConnection,
  completeConnectionIfValid,
  cancelConnection,
  // Nest
  initiateNestAction,
  getDependencyInfo,
  onCanvasContextMenu,
  onDismissCanvasContextMenu,
}) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [immediateNestAction, setImmediateNestAction] = useState(null);
  
  const { stopPanning } = panHook;
  
  // Selection box drag tracking
  const didSelectionBoxDragRef = useRef(false);
  const selectionBoxStartPosRef = useRef({ x: 0, y: 0 });
  // Right-click arms a selection box without rendering it, so the menu can paint first.
  const selectionArmedRef = useRef(false);
  const SELECTION_DRAG_THRESHOLD = 5;
  
  // Guard against double mouseup handling (React onMouseUp + document listener)
  const mouseUpHandledRef = useRef(false);
  
  // Refs for state values to avoid recreating callbacks
  const isSelectingBoxRef = useRef(isSelectingBox);
  isSelectingBoxRef.current = isSelectingBox;
  const isDraggingRef = useRef(isDragging);
  isDraggingRef.current = isDragging || !!draggingNodeRef?.current;

  const draggingNodeRefFromHook = draggingNodeRef;
  const isConnectingRef = useRef(isConnecting);
  isConnectingRef.current = isConnecting;

  const potentialDragNodeRef = useRef(potentialDragNode);
  potentialDragNodeRef.current = potentialDragNode;

  // Latest pointer while drawing a dependency (screen coords) — drives edge auto-pan + preview
  const connectionPointerRef = useRef({ x: 0, y: 0 });
  /** False until the first document mousemove after connection drag starts (avoids bogus edge-pan at 0,0). */
  const hasConnectionPointerRef = useRef(false);
  const updateConnectionTargetRef = useRef(updateConnectionTarget);
  updateConnectionTargetRef.current = updateConnectionTarget;

  // Fast pan hook - handles performance-critical panning
  const { fastPanActiveRef, syncLivePan, startFastPan } = useFastPan({
    containerRef,
    panHook,
    setPanAnimated,
    constrainPan,
    cancelPanCorrection,
    isPanningRef,
    zoomRef,
    clearSelection,
  });

  // Sync live pan position when not actively panning
  syncLivePan();

  // Auto-pan the viewport when the connection line is pulled near an edge (also keeps preview in sync when the pointer rests on an edge)
  useEffect(() => {
    if (!isConnecting) {
      hasConnectionPointerRef.current = false;
      return undefined;
    }

    hasConnectionPointerRef.current = false;

    let rafId = null;

    const tick = () => {
      if (!isConnectingRef.current) return;

      if (!hasConnectionPointerRef.current) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const container = containerRef.current;
      if (!container) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      const rect = container.getBoundingClientRect();
      const { x: clientX, y: clientY } = connectionPointerRef.current;
      const delta = computeEdgeAutoPanDelta(clientX, clientY, rect);

      if (delta.x !== 0 || delta.y !== 0) {
        cancelPanCorrection?.();
      }

      const pan = panHook.panRef.current;
      const z = zoomRef.current;
      let nextPan = pan;

      if (delta.x !== 0 || delta.y !== 0) {
        nextPan = constrainPan(
          { x: pan.x + delta.x, y: pan.y + delta.y },
          z,
        );
        setPan(nextPan);
      }

      const canvasPos = screenToCanvasUtil(clientX, clientY, containerRef, nextPan, z);
      setMousePos((prev) => {
        if (prev.x === canvasPos.x && prev.y === canvasPos.y) return prev;
        return canvasPos;
      });
      updateConnectionTargetRef.current(canvasPos.x, canvasPos.y);

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isConnecting, containerRef, panHook.panRef, zoomRef, setPan, constrainPan, cancelPanCorrection]);

  // ═══════════════════════════════════════════════════════════════
  // MOUSE MOVE - for non-pan interactions
  // ═══════════════════════════════════════════════════════════════

  const handleMouseMove = useCallback((e) => {
    // Skip if panning - handled by native listener in useFastPan
    if (fastPanActiveRef.current) return;

    if (isPointerOverPortaledOverlayFromEvent(e)) return;
    
    // CRITICAL: Block all interactions during animations
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker && blocker.style.display === 'block') {
      return;
    }
    
    // The press hasn't become a drag yet. Keep the menu up and skip the flowchart render.
    if (selectionArmedRef.current && !isSelectingBoxRef.current) {
      const dx = e.clientX - selectionBoxStartPosRef.current.x;
      const dy = e.clientY - selectionBoxStartPosRef.current.y;
      if (Math.abs(dx) > SELECTION_DRAG_THRESHOLD || Math.abs(dy) > SELECTION_DRAG_THRESHOLD) {
        selectionArmedRef.current = false;
        didSelectionBoxDragRef.current = true;
        onDismissCanvasContextMenu?.();
        startSelectionBox({
          clientX: selectionBoxStartPosRef.current.x,
          clientY: selectionBoxStartPosRef.current.y,
        });
      }
      return;
    }

    // Selection box
    if (isSelectingBoxRef.current) {
      const dx = e.clientX - selectionBoxStartPosRef.current.x;
      const dy = e.clientY - selectionBoxStartPosRef.current.y;
      if (Math.abs(dx) > SELECTION_DRAG_THRESHOLD || Math.abs(dy) > SELECTION_DRAG_THRESHOLD) {
        didSelectionBoxDragRef.current = true;
      }
      
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      setMousePos(canvasPos);
      updateSelectionBox(canvasPos);
      return;
    }

    // Check drag threshold — may start drag mid-frame before React re-renders isDragging
    if (potentialDragNode && !draggingNodeRefFromHook?.current) {
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      if (checkDragThreshold(e, canvasPos)) {
        setMousePos(canvasPos);
        if (isMissionToSidebarDragActive()) {
          updateMissionToSidebarPointer(e.clientX, e.clientY);
        }
        updateDragPosition(canvasPos, { clientX: e.clientX, clientY: e.clientY });
        return;
      }
    }

    // Update drag position (ref-based — no wait for isDragging state)
    if (draggingNodeRefFromHook?.current) {
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      setMousePos(canvasPos);
      if (isMissionToSidebarDragActive()) {
        updateMissionToSidebarPointer(e.clientX, e.clientY);
      }
      updateDragPosition(canvasPos, { clientX: e.clientX, clientY: e.clientY });
      return;
    }

    // Connection drag: pointer drives connectionPointerRef; RAF loop applies edge auto-pan + preview
    if (isConnectingRef.current) {
      connectionPointerRef.current = { x: e.clientX, y: e.clientY };
      hasConnectionPointerRef.current = true;
    }
  }, [screenToCanvas, updateSelectionBox, checkDragThreshold, updateDragPosition, potentialDragNode, draggingNodeRefFromHook, fastPanActiveRef, onDismissCanvasContextMenu, startSelectionBox]);

  // ═══════════════════════════════════════════════════════════════
  // MOUSE DOWN - starts pan or selection box
  // ═══════════════════════════════════════════════════════════════

  const handleCanvasMouseDown = useCallback((e) => {
    // Reset mouseup guard on new interaction (safety net for edge cases)
    mouseUpHandledRef.current = false;

    // Portaled modals bubble through React tree; ignore so we don't pan / marquee-select
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      return;
    }

    // Hit-test can return canvas while a full-screen modal still covers this point (stacking / pointer-events)
    if (isPointerOverPortaledOverlayFromEvent(e)) {
      return;
    }
    
    // CRITICAL: Block all interactions during animations
    // Check if the animation blocker is visible (display: block)
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker && blocker.style.display === 'block') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Right-click (or alt+click) starts selection box.
    // On empty space the menu unhides now, during the press. The selection box
    // render waits until the pointer actually drags, so it doesn't hold the paint.
    if (e.button === 2 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      selectionBoxStartPosRef.current = { x: e.clientX, y: e.clientY };
      didSelectionBoxDragRef.current = false;
      if (e.button === 2 && isContextMenuTarget(e.target, containerRef.current)) {
        selectionArmedRef.current = true;
        onCanvasContextMenu?.({
          screenPosition: { x: e.clientX, y: e.clientY },
          canvasPosition: screenToCanvas(e.clientX, e.clientY),
        });
        return;
      }
      selectionArmedRef.current = false;
      startSelectionBox(e);
      return;
    }
    
    // Left-click - check if we should start panning
    if (isPannableTarget(e.target, containerRef.current)) {
      startFastPan(e);
    }
  }, [containerRef, startSelectionBox, startFastPan, isPointerOverPortaledOverlayFromEvent, onCanvasContextMenu, screenToCanvas]);

  // ═══════════════════════════════════════════════════════════════
  // CONTEXT MENU - right-click on empty canvas
  // ═══════════════════════════════════════════════════════════════

  const handleContextMenu = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      return;
    }

    if (isPointerOverPortaledOverlayFromEvent(e)) {
      return;
    }

    e.preventDefault();
    
    // Don't show context menu if user dragged a selection box
    if (didSelectionBoxDragRef.current) {
      didSelectionBoxDragRef.current = false;
      return;
    }
    
    if (isContextMenuTarget(e.target, containerRef.current)) {
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      onCanvasContextMenu?.({
        screenPosition: { x: e.clientX, y: e.clientY },
        canvasPosition: canvasPos,
      });
    }
  }, [containerRef, screenToCanvas, onCanvasContextMenu]);

  // ═══════════════════════════════════════════════════════════════
  // MOUSE UP - completes interactions
  // ═══════════════════════════════════════════════════════════════

  const handleMouseUp = useCallback((e) => {
    // Guard: Prevent double handling from both React onMouseUp and document listener
    // Both fire for the same mouseup event, but we only want to process once
    if (mouseUpHandledRef.current) {
      return;
    }
    mouseUpHandledRef.current = true;
    selectionArmedRef.current = false;
    // Reset on next tick so future mouseups can be handled
    setTimeout(() => { mouseUpHandledRef.current = false; }, 0);
    
    // CRITICAL: Block all interactions during animations
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker && blocker.style.display === 'block') {
      return;
    }
    
    // Skip if this was a pan - handled by useFastPan
    if (fastPanActiveRef.current) return;

    // Bubbled mouseup from portaled modals — ignore unless a canvas interaction needs completion
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      const inProgress = draggingNodeRefFromHook?.current
        || isDraggingRef.current
        || isSelectingBoxRef.current
        || isConnectingRef.current
        || potentialDragNodeRef.current;
      if (!inProgress) {
        return;
      }
    }
    
    // Priority 1: Complete selection box
    if (completeSelectionBox(e)) return;

    // Get drag completion info (use last bridge pointer state — don't re-hit-test on mouseup)
    const { draggedNodeId, didDrag, dropTarget: completedDropTarget, draggedIds, sidebarDropPayload } = completeDrag({
      clientX: e.clientX,
      clientY: e.clientY,
    });
    
    if (sidebarDropPayload) {
      stopPanning();
      return;
    }
    
    // If dropped on a target, handle nesting
    if (completedDropTarget && didDrag && draggedIds.length > 0) {
      const draggedSet = new Set(draggedIds);
      const dependencyInfo = getDependencyInfo(draggedSet);
      const hasDependencies = dependencyInfo.upstreamCount > 0 || dependencyInfo.downstreamCount > 0;
      
      // If no external dependencies, skip modal and nest directly
      if (!hasDependencies) {
        setImmediateNestAction({
          targetTaskId: completedDropTarget.id,
          draggedIds,
          strategy: 'patch',
          dependencyInfo,
        });
        stopPanning();
        return;
      }
      
      // Has dependencies — show modal (positions already persisted in completeDrag)
      initiateNestAction(completedDropTarget, draggedIds);
      
      stopPanning();
      return;
    }
    
    // If this was a click (not a drag) on a node
    if (draggedNodeId && !didDrag) {
      if (connectingFrom) {
        completeConnection(draggedNodeId);
      } else {
        selectNode(draggedNodeId, e.shiftKey);
      }
    }
    
    completeConnectionIfValid();
    stopPanning();
  }, [fastPanActiveRef, completeSelectionBox, completeDrag, connectingFrom, completeConnection, selectNode, completeConnectionIfValid, stopPanning, getDependencyInfo, initiateNestAction]);

  // Clear immediate nest action
  const clearImmediateNestAction = useCallback(() => {
    setImmediateNestAction(null);
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // EVENT LISTENER MANAGEMENT
  // ═══════════════════════════════════════════════════════════════

  const isNonPanInteracting = isDragging || !!draggingNodeRefFromHook?.current || isSelectingBox || potentialDragNode || isConnecting;
  
  useEffect(() => {
    if (!isNonPanInteracting) return;
    
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isNonPanInteracting, handleMouseMove, handleMouseUp]);

  // Handle canvas click to cancel connections
  const handleCanvasClick = useCallback((e) => {
    // CRITICAL: Block all interactions during animations
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker && blocker.style.display === 'block') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    if (e.target === e.currentTarget || 
        e.target.classList.contains('flowchart__canvas') ||
        e.target.classList.contains('flowchart__canvas-grid') ||
        e.target.tagName === 'svg') {
      cancelConnection();
    }
  }, [cancelConnection]);

  return {
    mousePos,
    immediateNestAction,
    handleMouseMove,
    handleCanvasMouseDown,
    handleMouseUp,
    handleContextMenu,
    handleCanvasClick,
    clearImmediateNestAction,
  };
}
